import React from 'react'
import { Typography } from '@mui/joy'
import { COMMON_STYLES } from 'features/load-hub/utils/constants'
import { useMinuteTickCount } from 'hooks/useMinuteTick'
import { formatEasternDateTime } from 'utils/format-date'

const MINUTE_MS = 60000

interface AgeDisplayProps {
    postedAt: string | null
    // When true, render the exact posted time (US Eastern) instead of a relative age.
    // Sylectus exposes a precise post datetime, so dispatchers prefer seeing the clock time.
    exactTime?: boolean
}

/** The relative age of a load, in the largest unit that still reads as a number: 42m, 3h, 2d, 1w. */
const relativeAge = (postedAt: string): string => {
    // The database timestamp can be a few milliseconds ahead of the browser clock
    // for a just-delivered live load. Clamp it so a fractional negative minute never
    // renders as "-1m" after Math.floor.
    const diffMinutes = Math.max(0, (Date.now() - new Date(postedAt).getTime()) / MINUTE_MS)

    // Convert to appropriate time unit
    if (diffMinutes < 60) {
        // Less than 1 hour: show minutes
        return `${Math.floor(diffMinutes)}m`
    } else if (diffMinutes < 24 * 60) {
        // Less than 24 hours: show hours
        return `${Math.floor(diffMinutes / 60)}h`
    } else if (diffMinutes < 7 * 24 * 60) {
        // Less than 7 days: show days
        return `${Math.floor(diffMinutes / (60 * 24))}d`
    } else {
        // 7 days or more: show weeks
        return `${Math.floor(diffMinutes / (60 * 24 * 7))}w`
    }
}

/**
 * AgeDisplay component that updates the age of a load every minute
 * This prevents re-rendering the entire loads table when the age needs to update
 */
export const AgeDisplay: React.FC<AgeDisplayProps> = ({ postedAt, exactTime }) => {
    // Only a RELATIVE age moves on its own, so only those rows subscribe. A row with no posted time renders an
    // em dash and an `exactTime` row renders a fixed instant — both would otherwise re-render once a minute to
    // produce the same string, and `exactTime` is every Sylectus row on the Load Hub table.
    //
    // The count it returns is deliberately not read: the age below comes from the real clock on every render,
    // so a row scrolling into view mid-minute cannot inherit a stale shared reading.
    const ticking = !!postedAt && !exactTime
    useMinuteTickCount(ticking)

    // Exact-time mode: show the precise post datetime in US Eastern (Sylectus loadboard zone). Both branches
    // are cheap enough to run per render now that their formatters are built once rather than per call.
    const age = !postedAt ? '—' : exactTime ? (formatEasternDateTime(postedAt) ?? '—') : relativeAge(postedAt)

    return <Typography sx={COMMON_STYLES.TABLE_BODY_TEXT}>{age}</Typography>
}
